import { z } from 'zod';
import { Slug } from './common.js';
import { Role } from './roles.js';
import { Priority, StatusCategory, WorkItemType } from './work-items.js';
import { DueBucket, StatusColor } from './work.js';
/**
 * My work across all workspaces (docs/adr/0061-my-work-across-workspaces.md): one read-only list of the signed-in
 * person's own open work from every workspace they could open right now, built per workspace in that workspace's own
 * tenant context. Only the fields My work shows; withheld workspaces carry no data at all.
 */
/** Workspaces per page, and the most a person's list goes through in all (alphabetical). */
export const ALL_WORKSPACES_PAGE_SIZE = 5;
export const ALL_WORKSPACES_MAX = 50;
/** Items per workspace (the most urgent first); `openCount` says how many there are in all. */
export const ALL_WORKSPACES_ITEMS_PER_WORKSPACE = 50;
export const AllWorkspacesQuery = z.object({
    /** `nextCursor` from the previous page (an offset into the person's own list). */
    cursor: z.string().regex(/^\d{1,3}$/).optional(),
});
/** How a workspace is labelled: its name, and a colour (0–7, the avatar palette) and initials made from it. */
export const WorkspaceBadge = z.object({
    id: z.uuid(),
    name: z.string(),
    slug: Slug,
    color: z.number().int().min(0).max(7),
    initials: z.string(),
});
export const AllWorkspacesItem = z.object({
    id: z.uuid(),
    key: z.string(),
    type: WorkItemType,
    title: z.string(),
    priority: Priority,
    dueDate: z.string().nullable(),
    slaDueAt: z.string().nullable(),
    /** Worked out in the item's own workspace's time zone and week start. Never `done`: only open work is listed. */
    bucket: DueBucket,
    status: z.object({ label: z.string(), color: StatusColor, category: StatusCategory }),
    /** Empty when there is none. */
    projectName: z.string(),
    clientName: z.string(),
});
const base = { workspace: WorkspaceBadge, role: Role, current: z.boolean() };
/**
 * - `open`: the person could open it now; their items and counts.
 * - `tasks_off`: Tasks is switched off there, so nothing from it.
 * - `sign_in_required`: it requires single sign-on, or two-factor sign-in the person has not set up, that this sign-in
 *   does not satisfy ("Sign in to see"). No data.
 * - `not_joined`: an invitation they have not opened yet ("Open to join"). No data.
 * - `unavailable`: it could not be read just now. No data.
 */
export const AllWorkspacesSection = z.discriminatedUnion('access', [
    z.object({
        ...base,
        access: z.literal('open'),
        /** Read-only for billing (docs/adr/0044): still readable. */
        readOnly: z.boolean(),
        items: z.array(AllWorkspacesItem),
        openCount: z.number().int(),
        /** Unread mentions and replies, the count My work shows. */
        needsYou: z.number().int(),
        /** The person's own reminders due in the next 24 hours. */
        remindersSoon: z.number().int(),
    }),
    z.object({ ...base, access: z.literal('tasks_off') }),
    z.object({ ...base, access: z.literal('sign_in_required'), reason: z.enum(['sso', 'two_factor']) }),
    z.object({ ...base, access: z.literal('not_joined') }),
    z.object({ ...base, access: z.literal('unavailable') }),
]);
export const AllWorkspacesWork = z.object({
    sections: z.array(AllWorkspacesSection),
    nextCursor: z.string().nullable(),
    /** Workspaces in the person's list (at most ALL_WORKSPACES_MAX). */
    totalWorkspaces: z.number().int(),
    /** The person belongs to more than ALL_WORKSPACES_MAX workspaces; the rest are not listed. */
    capped: z.boolean(),
});
/** Groups for the apps, in order. `next_week` and `later` show together as "Later". */
export const ALL_WORKSPACES_GROUPS = [
    { key: 'overdue', label: 'Overdue', buckets: ['overdue'] },
    { key: 'today', label: 'Today', buckets: ['today'] },
    { key: 'this_week', label: 'This week', buckets: ['this_week'] },
    { key: 'later', label: 'Later', buckets: ['next_week', 'later'] },
    { key: 'no_date', label: 'No due date', buckets: ['no_date'] },
];
/** Items of one section in the apps' groups, empty groups left out. */
export function groupAllWorkspacesItems(items) {
    return ALL_WORKSPACES_GROUPS.map((g) => ({
        key: g.key,
        label: g.label,
        items: items.filter((i) => g.buckets.includes(i.bucket)),
    })).filter((g) => g.items.length > 0);
}
//# sourceMappingURL=all-workspaces.js.map